// src/encounter/diagnosis-multi-select.tsx
// The `DIAGNOSIS (n) *` block that EVERY A/P order dialog carries (PROD-1801).
//
// Source of truth — the LIVE Flutter app, captured 2026-07-30 (dev-provider.scrips.com, test
// patient) across all five order dialogs, and Figma Encounter-2 `1736:159053`
// (`appointments@patient@ap@edit-radiology`). Both agree:
//   • field label `DIAGNOSIS (n) *` — the count is LIVE (0 with none ticked, 2 with two) and the
//     asterisk is error-toned because at least one link is required;
//   • a "Hide Detail" text action on the same row, right-aligned;
//   • the encounter's diagnoses in a TWO-COLUMN grid, each row: term (primary text) above code
//     (muted), with a CIRCULAR selector on the right;
//   • multi-select — two of four ticked in the Figma frame, and the live app lets you tick any number.
//
// WHY THIS EXISTS AS AN APP COMPONENT rather than a DS one: the anatomy is generic but the CONTENT is
// this surface's — the encounter's own diagnosis list. The reusable parts are already DS
// (`Checkbox shape="circle"`, added in 1.58.0 for exactly this). If a second surface ever needs the
// same block, graduate it then; one consumer is not a DS component.
//
// Samer asked for this on 2026-07-29 ("we should have the diagnosis selected in the A/P and select
// this imaging under one or more from the selected diagnosis"). It was never a new requirement — the
// design has specified it since Jan '23 and React simply never built it. The persistence half landed
// separately (#269): an order now fans out to every linked diagnosis instead of silently keeping only
// the first, so ticking two here actually persists two.
import { useState } from 'react';
import { Checkbox } from '@scripsteam/scrips-signal-ds';

/** One selectable diagnosis — the encounter's A/P Dx cards, reduced to what this block renders. */
export interface DiagnosisOption {
  id: string;
  /** ICD-10 code. Absent on a free-text Dx, in which case only the term shows. */
  code?: string;
  display: string;
}

export interface DiagnosisMultiSelectProps {
  /** The encounter's diagnoses, in canvas order. Empty → the block renders its own empty state. */
  diagnoses: DiagnosisOption[];
  /** Currently linked diagnosis ids. */
  selectedIds: readonly string[];
  onChange: (selectedIds: string[]) => void;
  /**
   * Collapse the rows, leaving just the header — the frame's "Hide Detail" action.
   *
   * OPTIONAL AND UNCONTROLLED BY DEFAULT: pass neither and the block owns its own collapse state, so
   * the action shows up in every order dialog without each one plumbing a flag. (It was controlled-only
   * before, which meant no dialog passed it and the frame's action never rendered at all.) Pass both to
   * take control — e.g. to persist the state across dialog opens.
   */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  disabled?: boolean;
  /**
   * Field label. Defaults to `DIAGNOSIS` — the count and asterisk are appended by this component,
   * never by the caller, so the two never disagree.
   */
  label?: string;
  /**
   * `'default'` — the block above, unchanged, for every order dialog.
   * `'select-all'` — the ADD LAB popup's variant (canvas artboards 6-8; Andrew: lab popup only, the
   * shared default stays): no count, a "Select all" action in place of Hide/Show Detail, THREE
   * columns, and the round selector on the LEFT of the term.
   */
  variant?: 'default' | 'select-all';
}

const WRAP = 'font-sans';
const HEADER = 'flex items-baseline justify-between gap-3 mb-3';
// 12px = --text-eyebrow (`text-size-eyebrow`).
const LABEL =
  'text-size-eyebrow font-bold uppercase tracking-field-label text-text-hint';
const REQUIRED = 'ms-1 text-status-error';
// font-sans replaces the original `font: inherit` reset — this button already sits inside the
// app's sans-serif type context, so the rendered family is identical; `text-size-sm`/font-medium
// carry the size/weight that used to follow the shorthand.
const TOGGLE =
  'border-0 bg-transparent p-0 cursor-pointer font-sans text-size-sm font-medium ' +
  'text-brand-primary';
// Two columns per the frame; collapses to one below ~424px so the dialog stays usable narrow.
// `grid-cols-diagnoses` is the DS auto-fit minmax(200px, 1fr) track template (shared with the
// e-prescription diagnosis list): empty tracks collapse, cells stretch, and an odd last row keeps
// one track's width.
const GRID = 'grid grid-cols-diagnoses gap-x-6 gap-y-3';
const CELL = 'w-full';
// 13px = --text-sm; the traced 1.432 line-height is the --leading-reading token.
const TERM = 'text-size-sm leading-reading text-text-primary';
const CODE = 'text-size-sm leading-reading text-text-muted';
const EMPTY = 'text-size-sm text-text-muted py-2';
// The lab popup variant: 3 equal columns, 12px row / 24px column gap, selector left and top-aligned.
const GRID_3 = 'grid grid-cols-3 gap-x-6 gap-y-3';
const CELL_START = 'w-full !items-start';

export function DiagnosisMultiSelect({
  diagnoses,
  selectedIds,
  onChange,
  collapsed,
  onToggleCollapsed,
  disabled = false,
  label = 'Diagnosis',
  variant = 'default',
}: DiagnosisMultiSelectProps) {
  const selectAllVariant = variant === 'select-all';
  const selected = new Set(selectedIds);
  // Uncontrolled fallback (see `collapsed` above): the host may control the flag, but if it doesn't,
  // the block still offers the frame's Hide/Show Detail action.
  const [selfCollapsed, setSelfCollapsed] = useState(false);
  // `collapsed` (when given) owns the STATE; `onToggleCollapsed` (when given) is ALWAYS notified. The
  // two are independent on purpose — a host that only wants to observe the toggle shouldn't have to
  // take over the state, and a host that passes only the callback must still hear about the click.
  const isCollapsed = collapsed ?? selfCollapsed;
  const toggleCollapsed = () => {
    if (collapsed === undefined) setSelfCollapsed((c) => !c);
    onToggleCollapsed?.();
  };

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Emit in the diagnoses' own order, not click order — the save mapper treats dxIds[0] as the
    // nesting parent for single-link types, so a stable order keeps that parent deterministic.
    onChange(diagnoses.filter((d) => next.has(d.id)).map((d) => d.id));
  };

  return (
    <section data-slot="dx-select" className={WRAP} aria-label={`${label} selection`}>
      <div className={HEADER}>
        {/* Count is derived, never passed in — the label and the ticks cannot disagree. */}
        <span data-slot="dx-select-label" className={LABEL}>
          {selectAllVariant ? label : `${label} (${selected.size})`}
          <span className={REQUIRED} aria-hidden="true">
            *
          </span>
        </span>
        {diagnoses.length > 0 && selectAllVariant && (
          <button type="button" className={TOGGLE} onClick={() => onChange(diagnoses.map((d) => d.id))}>
            Select all
          </button>
        )}
        {diagnoses.length > 0 && !selectAllVariant && (
          <button
            type="button"
            data-slot="dx-select-toggle"
            className={TOGGLE}
            onClick={toggleCollapsed}
          >
            {isCollapsed ? 'Show Detail' : 'Hide Detail'}
          </button>
        )}
      </div>

      {diagnoses.length === 0 ? (
        // Anti-fiction: no diagnoses means the order genuinely cannot be linked yet. Say so rather
        // than render an empty grid that looks like a loading state.
        <p className={EMPTY}>Add a diagnosis on the canvas before linking this order.</p>
      ) : selectAllVariant ? (
        <div className={GRID_3} role="group" aria-label={label}>
          {diagnoses.map((d) => (
            <Checkbox
              key={d.id}
              shape="circle"
              checked={selected.has(d.id)}
              disabled={disabled}
              onCheckedChange={() => toggle(d.id)}
              className={CELL_START}
              label={
                <span className="block">
                  <span className={`block ${TERM}`}>{d.display}</span>
                  {d.code && <span className={`block ${CODE}`}>{d.code}</span>}
                </span>
              }
            />
          ))}
        </div>
      ) : isCollapsed ? null : (
        <div data-slot="dx-select-grid" className={GRID} role="group" aria-label={label}>
          {/*
            CIRCULAR by design — the Figma selectors are circles (16px box, 11px radius), which is
              why `shape="circle"` was added to the DS Checkbox in 1.58.0. Still a CHECKBOX:
              multi-select, never a radio.

              THE TERM IS THE CHECKBOX'S OWN `label`, DELIBERATELY — not a sibling node: Figma makes
              the whole ROW the target, term on the left, circle on the right, and putting the term in
              the label is what makes the row (not just the 16px circle) clickable and correctly named
              for a screen reader. `row-reverse` then puts the circle on the right per the frame.

              CORRECTION (2026-07-30): an earlier version of this comment gave a second, load-bearing
              reason — a "known DS defect, pinned in scrips-signal-ds #297: clicking the BOX does not
              toggle". THAT DEFECT DOES NOT EXIST. Samer traced it to jsdom lacking `PointerEvent` in
              the signal-ds test env (signal-ds #303), and I verified it here rather than taking it on
              faith: `label[for]` resolves to the hidden `<input type=checkbox>`, NOT to the visual
              `<span role=checkbox>` — two different ids — so the double-toggle the pin described has
              no mechanism, and a click on the box fires `onCheckedChange` exactly once. The layout
              above is still right, but it is a design choice, not a workaround; don't treat it as one.
          */}
          {diagnoses.map((d) => (
            <Checkbox
              key={d.id}
              shape="circle"
              checked={selected.has(d.id)}
              disabled={disabled}
              onCheckedChange={() => toggle(d.id)}
              className={CELL}
              labelPosition="start"
              label={
                <span className="block">
                  <span data-slot="dx-select-term" className={TERM}>
                    {d.display}
                  </span>
                  {d.code && (
                    <>
                      <br />
                      <span data-slot="dx-select-code" className={CODE}>
                        {d.code}
                      </span>
                    </>
                  )}
                </span>
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}
